import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Card,
  CardContent,
} from '@mui/material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import {
  Download as DownloadIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  DateRange as DateRangeIcon,
  Agriculture as AgricultureIcon,
  LocationOn as LocationIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import api from '../services/api';

interface SeedSurvey {
  id: number;
  survey_number: string;
  date_collected: string;
  crop_name_ar: string;
  crop_name_en: string;
  crop_scientific_name: string;
  farmer_name: string;
  farmer_civilid: string;
  farmer_phone: string;
  village_name_ar: string;
  village_name_en: string;
  state_name_ar: string;
  state_name_en: string;
  governorate_name_ar: string;
  governorate_name_en: string;
  usage_name_ar: string;
  usage_name_en: string;
  sample_type_name_ar: string;
  sample_type_name_en: string;
  genetic_status_name_ar: string;
  genetic_status_name_en: string;
  is_local_seed: boolean;
  local_name: string;
  elevation: number;
  auto_latitude: number;
  auto_longitude: number;
  is_herbarium_sample: boolean;
  has_photos: boolean;
  notes: string;
  created_at: string;
  created_by_username: string;
}

interface Statistics {
  total_surveys: number;
  total_farmers: number;
  total_crops: number;
  total_villages: number;
  local_seeds: number;
  herbarium_samples: number;
  with_photos: number;
}

interface FilterParams {
  date_from: Date | null;
  date_to: Date | null;
  crop: string;
  governorate: string;
  state: string;
  village: string;
  farmer: string;
  is_local_seed: string;
  search: string;
}

const SeedReportsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [surveys, setSurveys] = useState<SeedSurvey[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  });
  
  // Filter states
  const [filters, setFilters] = useState<FilterParams>({
    date_from: null,
    date_to: null,
    crop: '',
    governorate: '',
    state: '',
    village: '',
    farmer: '',
    is_local_seed: '',
    search: '',
  });
  
  // Options for dropdowns
  const [crops, setCrops] = useState<any[]>([]);
  const [governorates, setGovernorates] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [villages, setVillages] = useState<any[]>([]);
  const [farmers, setFarmers] = useState<any[]>([]);
  
  useEffect(() => {
    fetchOptions();
    fetchSurveys();
  }, []);
  
  useEffect(() => {
    fetchSurveys();
  }, [filters, paginationModel]);
  
  const fetchOptions = async () => {
    try {
      const [cropsRes, governoratesRes, farmersRes] = await Promise.all([
        api.get('/crops/'),
        api.get('/governorates/'),
        api.get('/seeds/farmers/'),
      ]);
      
      setCrops(cropsRes.data.results || cropsRes.data);
      setGovernorates(governoratesRes.data.results || governoratesRes.data);
      setFarmers(farmersRes.data.results || farmersRes.data);
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  };
  
  const fetchSurveys = async () => {
    try {
      setLoading(true);
      
      // Build query params
      const params: any = {
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
      };
      
      if (filters.date_from) {
        params.date_from = format(filters.date_from, 'yyyy-MM-dd');
      }
      if (filters.date_to) {
        params.date_to = format(filters.date_to, 'yyyy-MM-dd');
      }
      if (filters.crop) params.crop = filters.crop;
      if (filters.governorate) params.governorate = filters.governorate;
      if (filters.state) params.state = filters.state;
      if (filters.village) params.village = filters.village;
      if (filters.farmer) params.farmer = filters.farmer;
      if (filters.is_local_seed) params.is_local_seed = filters.is_local_seed;
      if (filters.search) params.search = filters.search;
      
      const [surveysRes, statsRes] = await Promise.all([
        api.get('/reports/seeds/', { params }),
        api.get('/reports/seeds/statistics/', { params }),
      ]);
      
      setSurveys(surveysRes.data.results || []);
      setStatistics(statsRes.data);
    } catch (error) {
      console.error('Error fetching surveys:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      
      // إضافة اللغة الحالية
      params.append('language', i18n.language);
      
      // إضافة الفلاتر
      if (filters.date_from) {
        params.append('date_from', format(filters.date_from, 'yyyy-MM-dd'));
      }
      if (filters.date_to) {
        params.append('date_to', format(filters.date_to, 'yyyy-MM-dd'));
      }
      if (filters.crop) {
        params.append('crop', filters.crop);
      }
      if (filters.governorate) {
        params.append('governorate', filters.governorate);
      }
      if (filters.state) {
        params.append('state', filters.state);
      }
      if (filters.village) {
        params.append('village', filters.village);
      }
      if (filters.farmer) {
        params.append('farmer', filters.farmer);
      }
      if (filters.is_local_seed !== '') {
        params.append('is_local_seed', filters.is_local_seed);
      }
      if (filters.search) {
        params.append('search', filters.search);
      }

      const response = await api.get(`/reports/seeds/export_excel/?${params.toString()}`, {
        responseType: 'blob'
      });

      // إنشاء رابط التحميل مع اسم ملف حسب اللغة
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const fileName = i18n.language === 'ar' 
        ? `تقرير_البذور_${format(new Date(), 'yyyy-MM-dd')}.xlsx`
        : `seed_reports_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // enqueueSnackbar(t('exportSuccess'), { variant: 'success' });
    } catch (error) {
      console.error('Export error:', error);
      // enqueueSnackbar(t('exportError'), { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };
  
  const handleFilterChange = (field: keyof FilterParams, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    
    // Load dependent dropdowns
    if (field === 'governorate' && value) {
      fetchStates(value);
    }
    if (field === 'state' && value) {
      fetchVillages(value);
    }
  };
  
  const fetchStates = async (governorateId: string) => {
    try {
      const response = await api.get(`/states/by_governorate/?governorate_id=${governorateId}`);
      setStates(response.data);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };
  
  const fetchVillages = async (stateId: string) => {
    try {
      const response = await api.get(`/villages/by_state/?state_id=${stateId}`);
      setVillages(response.data);
    } catch (error) {
      console.error('Error fetching villages:', error);
    }
  };
  
  const clearFilters = () => {
    setFilters({
      date_from: null,
      date_to: null,
      crop: '',
      governorate: '',
      state: '',
      village: '',
      farmer: '',
      is_local_seed: '',
      search: '',
    });
    setStates([]);
    setVillages([]);
  };
  
  const columns: GridColDef[] = [
    {
      field: 'survey_number',
      headerName: t('surveyNumber'),
      width: 150,
      renderCell: (params) => (
        <Chip label={params.value} size="small" color="primary" />
      ),
    },
    {
      field: 'date_collected',
      headerName: t('dateCollected'),
      width: 120,
      valueFormatter: (params) => {
        if (!params.value) return '';
        return format(new Date(params.value), 'yyyy-MM-dd');
      },
    },
    {
      field: 'crop',
      headerName: t('crop'),
      width: 150,
      valueGetter: (params) => {
        return isRtl ? params.row.crop_name_ar : params.row.crop_name_en;
      },
    },
    {
      field: 'farmer',
      headerName: t('farmer'),
      width: 150,
      valueGetter: (params) => params.row.farmer_name || '',
    },
    {
      field: 'farmer_civilid',
      headerName: t('civilId'),
      width: 120,
    },
    {
      field: 'village',
      headerName: t('village'),
      width: 150,
      valueGetter: (params) => {
        return isRtl ? params.row.village_name_ar : params.row.village_name_en;
      },
    },
    {
      field: 'state',
      headerName: t('state'),
      width: 150,
      valueGetter: (params) => {
        return isRtl ? params.row.state_name_ar : params.row.state_name_en;
      },
    },
    {
      field: 'governorate',
      headerName: t('governorate'),
      width: 150,
      valueGetter: (params) => {
        return isRtl ? params.row.governorate_name_ar : params.row.governorate_name_en;
      },
    },
    {
      field: 'is_local_seed',
      headerName: t('localSeed'),
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('yes') : t('no')}
          size="small"
          color={params.value ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'has_photos',
      headerName: t('hasPhotos'),
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('yes') : t('no')}
          size="small"
          color={params.value ? 'info' : 'default'}
        />
      ),
    },
  ];
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            {t('seedReports')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('seedReportsDescription')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="success"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? <CircularProgress size={20} /> : t('exportExcel')}
          </Button>
          <IconButton onClick={fetchSurveys} color="primary">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>
      
      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {statistics.total_surveys}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('totalSurveys')}
                    </Typography>
                  </Box>
                  <AssessmentIcon sx={{ fontSize: 40, color: 'primary.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {statistics.total_farmers}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('totalFarmers')}
                    </Typography>
                  </Box>
                  <AgricultureIcon sx={{ fontSize: 40, color: 'success.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {statistics.total_crops}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('totalCrops')}
                    </Typography>
                  </Box>
                  <AgricultureIcon sx={{ fontSize: 40, color: 'warning.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {statistics.local_seeds}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('localSeeds')}
                    </Typography>
                  </Box>
                  <LocationIcon sx={{ fontSize: 40, color: 'info.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterIcon sx={{ mr: 1 }} />
          <Typography variant="h6">{t('filters')}</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={clearFilters} size="small">
            {t('clearFilters')}
          </Button>
        </Box>
        
        <Grid container spacing={2}>
          {/* Date Range */}
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t('dateFrom')}
                value={filters.date_from}
                onChange={(value) => handleFilterChange('date_from', value)}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t('dateTo')}
                value={filters.date_to}
                onChange={(value) => handleFilterChange('date_to', value)}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </LocalizationProvider>
          </Grid>
          
          {/* Crop */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('crop')}</InputLabel>
              <Select
                value={filters.crop}
                onChange={(e) => handleFilterChange('crop', e.target.value)}
                label={t('crop')}
              >
                <MenuItem value="">{t('all')}</MenuItem>
                {crops.map((crop) => (
                  <MenuItem key={crop.id} value={crop.id}>
                    {isRtl ? crop.name_ar : crop.name_en}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Governorate */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('governorate')}</InputLabel>
              <Select
                value={filters.governorate}
                onChange={(e) => handleFilterChange('governorate', e.target.value)}
                label={t('governorate')}
              >
                <MenuItem value="">{t('all')}</MenuItem>
                {governorates.map((gov) => (
                  <MenuItem key={gov.id} value={gov.id}>
                    {isRtl ? gov.name_ar : gov.name_en}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* State */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small" disabled={!filters.governorate}>
              <InputLabel>{t('state')}</InputLabel>
              <Select
                value={filters.state}
                onChange={(e) => handleFilterChange('state', e.target.value)}
                label={t('state')}
              >
                <MenuItem value="">{t('all')}</MenuItem>
                {states.map((state) => (
                  <MenuItem key={state.id} value={state.id}>
                    {isRtl ? state.name_ar : state.name_en}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Village */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small" disabled={!filters.state}>
              <InputLabel>{t('village')}</InputLabel>
              <Select
                value={filters.village}
                onChange={(e) => handleFilterChange('village', e.target.value)}
                label={t('village')}
              >
                <MenuItem value="">{t('all')}</MenuItem>
                {villages.map((village) => (
                  <MenuItem key={village.id} value={village.id}>
                    {isRtl ? village.name_ar : village.name_en}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Farmer */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('farmer')}</InputLabel>
              <Select
                value={filters.farmer}
                onChange={(e) => handleFilterChange('farmer', e.target.value)}
                label={t('farmer')}
              >
                <MenuItem value="">{t('all')}</MenuItem>
                {farmers.map((farmer) => (
                  <MenuItem key={farmer.id} value={farmer.id}>
                    {farmer.name} - {farmer.civilid}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Local Seed */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('localSeed')}</InputLabel>
              <Select
                value={filters.is_local_seed}
                onChange={(e) => handleFilterChange('is_local_seed', e.target.value)}
                label={t('localSeed')}
              >
                <MenuItem value="">{t('all')}</MenuItem>
                <MenuItem value="true">{t('yes')}</MenuItem>
                <MenuItem value="false">{t('no')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Search */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label={t('search')}
              placeholder={t('searchPlaceholder')}
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              InputProps={{
                startAdornment: <FilterIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
        </Grid>
      </Paper>
      
      {/* Data Grid */}
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={surveys}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          loading={loading}
          disableRowSelectionOnClick
          slots={{ toolbar: GridToolbar }}
          sx={{
            '& .MuiDataGrid-root': {
              border: 'none',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(224, 224, 224, 1)',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'background.paper',
              borderBottom: '2px solid',
              borderColor: 'divider',
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '2px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default SeedReportsPage;
